import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { BASE_SCALAR_LAYER_IDS, OVERLAY_LAYER_IDS } from '../config/weatherLayers';

/** الافتراضي: الخطوط ظاهرة على طبقة الضغط فقط، ومغلقة على البقية حتى يُشغّلها المستخدم. */
import { createDefaultLayerAnimationSettings } from '../config/layerAnimation';
import { closeOverlayQuickPanel, normalizeLongitude, getResponsivePanelPatch, isCompactViewport } from './helpers';
import { readHomeLocation } from '../services/ipLocationService';
import type { LayerKey, WeatherState } from './types';
import { getHomeZoom } from '../config/mapZoom';

// إعادة تصدير الأنواع للحفاظ على توافق الاستيراد من '../store/weatherStore'.
export type {
    WeatherHourlyData, WeatherData, MapBounds, LayerKey, WeatherModel, WeatherState,
} from './types';
export { WEATHER_MODELS } from './types';

// مفتاح تخزين تفضيلات المستخدم في localStorage (نُسخة v1).
export const USER_PREFS_STORAGE_KEY = 'rasid:user-prefs';

function readPersistedState(): any | null {
    try {
        const raw = localStorage.getItem(USER_PREFS_STORAGE_KEY);
        return raw ? JSON.parse(raw)?.state ?? null : null;
    } catch {
        return null;
    }
}

// تُلتقط القيم لحظة تحميل الوحدة — أي قبل إنشاء المتجر وقبل أن يكتب persist أي شيء
// للتخزين — كي لا يخلط أول set() في الجلسة الحالية بين "محفوظ من زيارة سابقة" و"كُتب الآن".
const persistedAtBoot = readPersistedState();

/** هل توجد إعدادات جسيمات محفوظة للمستخدم؟ تُستخدم كي لا تدوسها قيم الأدمن البعيدة. */
export function hasPersistedAnimationSettings(): boolean {
    return Boolean(persistedAtBoot?.layerAnimationSettings);
}


// رابطٌ عميق ?layer=radar|satellite من صفحتَي الهبوط (/rain-radar/، /satellite-map/):
// القادم من بحث «رادار الأمطار» يجب أن يرى الرادار، لا الرياح الافتراضية ولا آخر طبقة
// محفوظة. يُطبَّق على الحالة الأولى وعلى المحفوظة معاً (الزائر الجديد لا حالة محفوظة له
// فلا يمرّ بـ merge)، مرّةً عند التحميل فقط، ثم يعود الاختيار للمستخدم.
function withLayerLink(v: Record<LayerKey, boolean>): Record<LayerKey, boolean> {
    let want: string | null = null;
    try { want = new URLSearchParams(window.location.search).get('layer'); } catch { /* بلا window */ }
    if (want !== 'radar' && want !== 'satellite') return v;
    const out = { ...v };
    BASE_SCALAR_LAYER_IDS.forEach((l) => { out[l] = false; });
    out.radar = want === 'radar';
    out.satellite = want === 'satellite';
    return out;
}

export const useWeatherStore = create<WeatherState>()(persist((set) => ({
    // الموقع الافتتاحي: مدينة المستخدم المحفوظة (مكتشفة عبر IP) — وإلا الرياض مؤقتاً
    // حتى يكتمل الكشف عند أول زيارة (راجع NewWeatherMap).
    currentLocation: readHomeLocation() ?? { lat: 24.7136, lon: 46.6753 },
    setCurrentLocation: (lat, lon) => set({ currentLocation: { lat, lon: normalizeLongitude(lon) } }),
    locationPreviewRequest: 0,
    requestLocationPreview: () => set({ locationPreviewRequest: Date.now() }),

    mapBounds: null,
    setMapBounds: (bounds) => set({ mapBounds: bounds }),
    // الزوم المبدئي يُحسب من عرض الشاشة (راجع getHomeZoom): الرقم الثابت 6 كان يعني
    // ~1756 كم على سطح المكتب و~476 كم على الهاتف — نفس الرقم، تغطيتان مختلفتان تماماً.
    zoomLevel: getHomeZoom(),
    setZoomLevel: (zoom) => set({ zoomLevel: zoom }),

    // ثلاثة نماذج عالمية لها raster مُولَّد على السيرفر: GFS (NOAA)، ECMWF IFS (open data)،
    // و ICON (DWD opendata، يُعاد تشبيكه من الشبكة icosahedral إلى lat/lon منتظمة عبر فهرس
    // أقرب-جار مُخزَّن لمرّة واحدة — راجع backend/scripts/icon_to_raster.py).
    // الافتراضي بلا باك إند: GFS و ECMWF (ما كان يعرضه ModelSelector سابقاً).
    // القائمة الفعلية تُرطَّب من إعدادات الأدمن (/api/v1/config) عبر applyRemoteModels —
    // ومنه يمكن تفعيل ICON (raster له مولَّد عبر فهرس أقرب-جار، راجع icon_to_raster.py).
    availableModels: [
        { id: 'GFS', name: 'GFS', resolutionKm: 22, color: '#2196F3' },
        // 22 كم لا 9 كم: بيانات ECMWF المجانية (open data) تُنشر على شبكة 0.25° — نفس شبكة
        // GFS تماماً (راجع ترويسة ecmwf_to_raster.py). الـ 9 كم هي دقّة IFS التشغيلي
        // المرخَّص الذي يعرضه Windy، ولا نملكه. إعلانها 9 كم يَعِد المستخدم بدقّة ثلاثة
        // أضعاف ما يراه فعلاً. (يمكن لداشبورد الأدمن تجاوز هذا عبر resolution_label.)
        { id: 'ECMWF', name: 'ECMWF', resolutionKm: 22, color: '#4CAF50' },
        // ── ICON مُعطَّل: قيدُ سعةٍ مقيس لا قرار منتج ─────────────────────────────
        // هو الأدقّ شبكيّاً (0.125° ≈ 13 كم مقابل 0.25° للاثنين أعلاه)، لكنه أغلى
        // النماذج بفارق كبير: قياس 2026-09-01 على أربعة أنوية أعطى 4س58د للدورة
        // الواحدة، أي 10 ساعات معالج يومياً بدورتين — من أصل 28 ساعة عملٍ مطلوبة في
        // يومٍ من 24. فكان يجوّع نفسه وجاريه معاً: بلغت دورة ICON يومين عمراً وشاخ
        // GFS معها. حذفه يُنزل المجموع إلى 18 ساعة، فيتّسع اليوم للاثنين الباقيين
        // بهامش ساعة ونصف في كل نافذة ستّ ساعات.
        //
        // اختير هو دون غيره لأنه الأسوأ في نسبة الكلفة إلى القيمة: تغطيته عالمية
        // مكرَّرة مع GFS وECMWF، وقيمة GFS (أربع دورات ⇒ الأحدث) وقيمة ECMWF (الأدقّ
        // عالمياً بـ1س16د فقط للدورة) كلتاهما أعلى.
        //
        // ── لإعادته عند ترقية الخادم إلى ثمانية أنوية ─────────────────────────────
        // أعِد السطر أدناه، وأزِل التعليق عن سطر ICON في crontab الخادم، وفهرس
        // أقرب-جار محفوظ في /root/icon-index (فلا يُعاد بناؤه).
        // { id: 'ICON', name: 'ICON', resolutionKm: 13, color: '#FF9800' },
    ],
    selectedModel: 'GFS',
    setSelectedModel: (model) => set({ selectedModel: model }),
    initializeModels: async () => {
        // القائمة أعلاه احتياطية فقط (بلا باك إند). القائمة الفعلية تأتي من
        // /api/v1/config عبر applyRemoteModels، وهي التي تحكم ما يظهر في المُحدِّد.
    },

    visibleLayers: withLayerLink({
        satellite: false,
        precipitation: false,
        // الطبقة الافتراضية عند أول زيارة: الرياح — كما تفتح Windy تماماً. الحركة هي
        // أوّل ما يقول للزائر الجديد "هذه خريطة حيّة لا صورة"، والحرارة لوحٌ ساكن.
        // الزيارات التالية تفتح على آخر طبقة اختارها المستخدم (visibleLayers محفوظة).
        wind: true,
        'wind-gusts': false,
        'radar-sim': false,
        temperature: false,
        'feels-like': false,
        'wet-bulb': false,
        'k-index': false,
        'o-index': false,
        pressure: false,
        humidity: false,
        dewpoint: false,
        clouds: false,
        't850-anomaly': false,
        'sst-anomaly': false,
        'sst-anomaly-rel': false,
        radar: false,
        // الأعاصير ظاهرة افتراضياً (نمط Zoom Earth) — نفس المفتاح الذي يبدّله الزر،
        // كي يعمل الإظهار/الإخفاء بشكل متسق.
        hurricanes: true,
        wildfires: false
    }),
    layerAnimationSettings: createDefaultLayerAnimationSettings(),
    updateLayerAnimationSettings: (layer, settings) => set((state) => ({
        layerAnimationSettings: {
            ...state.layerAnimationSettings,
            [layer]: { ...state.layerAnimationSettings[layer], ...settings },
        },
    })),
    setLayerParticlesEnabled: (layer, enabled) => set((state) => ({
        layerAnimationSettings: {
            ...state.layerAnimationSettings,
            [layer]: { ...state.layerAnimationSettings[layer], particlesEnabled: enabled },
        },
    })),
    setActiveLayer: (layer) => set((state) => {
        // إخفاء جميع طبقات الخريطة الحرارية، وتفعيل المختارة فقط
        // كل الطبقات السلَّمية القاعدية تتبادل، لا طبقات النموذج وحدها — راجع
        // BASE_SCALAR_LAYER_IDS لسبب التمييز.
        const exclusive: LayerKey[] = [...BASE_SCALAR_LAYER_IDS];
        const newLayers = { ...state.visibleLayers };
        if (layer === 'satellite') {
            // القمر الصناعي صورة قاعدية بديلة: تشغيله يوقف الطبقات **القاعدية** الأخرى
            // فقط — أمّا التراكبات (أعاصير/حرائق) فتبقى على حالها وتُرسم فوقه.
            const turningOn = !newLayers.satellite;
            exclusive.forEach(l => { newLayers[l] = false; });
            newLayers.radar = false;
            newLayers.satellite = turningOn;
        } else if (layer === 'radar') {
            // الرادار قاعدة قائمة بذاتها — نفس معاملة القمر تماماً: يُطفئ كل الحقول
            // السلَّمية والقمر، ويبقى وحده. والتراكبات (أعاصير/حرائق/تحذيرات) فوقه.
            const turningOn = !newLayers.radar;
            exclusive.forEach(l => { newLayers[l] = false; });
            newLayers.satellite = false;
            newLayers.radar = turningOn;
        } else if (exclusive.includes(layer)) {
            exclusive.forEach(l => { newLayers[l] = false; });
            newLayers[layer] = true;
            newLayers.satellite = false;
            // وحقلٌ سلَّميّ يُطفئ الرادار: بلاطات مرصودة معلّقة فوق حقل حرارة لا معنى لها.
            newLayers.radar = false;
        } else if (OVERLAY_LAYER_IDS.includes(layer)) {
            // **تراكبات لا طبقات قاعدة.** الأعاصير والحرائق رموز تُرسم فوق أي خلفية، فلا
            // معنى لأن يُطفئ تشغيلُها صورةَ القمر (كان يفعل ذلك، فتختفي الصورة عند الضغط
            // على أيٍّ منهما). تبديلها لا يمسّ أي طبقة أخرى إطلاقاً.
            newLayers[layer] = !newLayers[layer];
        } else {
            newLayers[layer] = !newLayers[layer];
            newLayers.satellite = false;
        }
        return { visibleLayers: newLayers };
    }),
    toggleLayer: (layer) => set((state) => ({
        visibleLayers: {
            ...state.visibleLayers,
            [layer]: !state.visibleLayers[layer]
        }
    })),
    closeAllLayers: () => set((state) => {
        const newLayers = { ...state.visibleLayers };
        Object.keys(newLayers).forEach(k => { newLayers[k as LayerKey] = false; });
        return { visibleLayers: newLayers };
    }),

    currentTimeIndex: 0,
    setCurrentTimeIndex: (index) => set({ currentTimeIndex: index }),
    frameFraction: 0,
    setFrameFraction: (frac) => set({ frameFraction: frac }),
    setTimelinePosition: (index, fraction) => set({ currentTimeIndex: index, frameFraction: fraction }),
    timelineSeeking: false,
    setTimelineSeeking: (seeking) => set({ timelineSeeking: seeking }),
    timelineSeekRevision: 0,
    commitTimelineSeek: () => set((state) => ({ timelineSeekRevision: state.timelineSeekRevision + 1 })),
    timelineAutoSync: true,
    setTimelineAutoSync: (enabled) => set({ timelineAutoSync: enabled }),
    isPlaying: false,
    setIsPlaying: (playing) => set({ isPlaying: playing }),
    playbackSpeed: 1,
    setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),

    weatherData: null,
    setWeatherData: (data) => set({ weatherData: data }),
    isLoading: false,
    setIsLoading: (loading) => set({ isLoading: loading }),

    units: {
        temperature: 'celsius',
        wind: 'kmh',
        pressure: 'hPa',
        precipitation: 'mm'
    },
    setUnits: (units) => set((state) => ({
        units: { ...state.units, ...units }
    })),
    darkMode: true,
    setDarkMode: (dark) => set({ darkMode: dark }),

    // ظاهرة افتراضياً (نمط Zoom Earth: أيقونات الأعاصير النشطة فوق كل الطبقات).
    showHurricanes: true,
    setShowHurricanes: (show) => set({ showHurricanes: show }),
    showWildfires: false,
    setShowWildfires: (show) => set({ showWildfires: show }),
    mapLabelsEnabled: true,
    setMapLabelsEnabled: (enabled) => set({ mapLabelsEnabled: enabled }),
    crosshairEnabled: true,
    setCrosshairEnabled: (enabled) => set({ crosshairEnabled: enabled }),
    labelValuesEnabled: true,
    setLabelValuesEnabled: (enabled) => set({ labelValuesEnabled: enabled }),
    lightningEnabled: false,
    setLightningEnabled: (enabled) => set({ lightningEnabled: enabled }),
    // تعمل دائماً: أُزيل مفتاحها من لوحة التراكبات، فالافتراضي هو السلوك الوحيد.
    alertsEnabled: true,
    setAlertsEnabled: (enabled) => set({ alertsEnabled: enabled }),
    // قناع تغطية الرادار — مُشغَّل افتراضياً لأنه يحمل معلومةً لا زينة: بدونه تبدو
    // «لا مطر هنا» و«لا رادار هنا» متطابقتين، وهما نقيضان. ومع ذلك يُطفأ بخيار:
    // من يعرف حدود التغطية سلفاً يفضّل خريطةً بلا أي حجاب.
    radarCoverageEnabled: true,
    setRadarCoverageEnabled: (enabled) => set({ radarCoverageEnabled: enabled }),
    // مُطفأ افتراضياً: المتصفّح يمنع الصوت قبل لمسة، وصوتٌ مفاجئ عند فتح الموقع مزعج.
    lightningSoundEnabled: false,
    setLightningSoundEnabled: (enabled) => set({ lightningSoundEnabled: enabled }),

    // ختم الوقت أعلى الخريطة — لمن يلتقط صورة الشاشة وينشرها. مطفأ افتراضياً:
    // الشريط السفلي يكفي من هو أمام الشاشة، والختم يخصّ الصورة لا الصفحة.
    timestampBadgeEnabled: false,
    setTimestampBadgeEnabled: (enabled) => set({ timestampBadgeEnabled: enabled }),
    // مُشغَّلة افتراضياً: هي ما يجعل الرادار يُقرأ مكاناً لا لطخةً ملوّنة، وهو أسلوب
    // WeatherFront وRadarScope. ومن يفضّل الرمادي يُطفئها ويُحفَظ اختياره.
    aerialImageryEnabled: true,
    setAerialImageryEnabled: (enabled) => set({ aerialImageryEnabled: enabled }),

    satelliteMode: 'live',
    setSatelliteMode: (mode) => set({ satelliteMode: mode }),

    sidebarOpen: false,
    setSidebarOpen: (open) => {
        if (open) closeOverlayQuickPanel();
        set(open ? getResponsivePanelPatch('sidebarOpen') : { sidebarOpen: false });
    },
    // منتقي طبقات الطقس يبدأ مطوياً في كل المقاسات: الخريطة هي الشاشة الرئيسية،
    // أما التراكبات السريعة فلها زرها العائم المستقل كما في Zoom Earth.
    layerControlsOpen: false,
    setLayerControlsOpen: (open) => {
        if (open) closeOverlayQuickPanel();
        // مرسى الأعاصير يمين كلوحة الطبقات (2026-09-13)، فهما متنافيان على كل المقاسات.
        set(open ? { ...getResponsivePanelPatch('layerControlsOpen'), cycloneDockOpen: false } : { layerControlsOpen: false });
    },
    infoPanelOpen: false,
    // لوحة الطقس ومرسى الأعاصير يتقاسمان العمود نفسه بجوار الشريط الرأسي، فهما متنافيان
    // على **كل** المقاسات لا على الجوّال وحده.
    setInfoPanelOpen: (open) => set(open
        ? (closeOverlayQuickPanel(), { ...getResponsivePanelPatch('infoPanelOpen'), cycloneDockOpen: false })
        : { infoPanelOpen: false }),
    settingsOpen: false,
    setSettingsOpen: (open) => {
        if (open) closeOverlayQuickPanel();
        set(open ? getResponsivePanelPatch('settingsOpen') : { settingsOpen: false });
    },
    cycloneDockOpen: false,
    setCycloneDockOpen: (open) => set(open
        ? (closeOverlayQuickPanel(), { ...getResponsivePanelPatch('cycloneDockOpen'), infoPanelOpen: false, layerControlsOpen: false })
        : { cycloneDockOpen: false }),
    searchOpen: false,
    setSearchOpen: (open) => set({ searchOpen: open }),
    // تشغيل القياس يُخلي الخريطة: اللوحات المفتوحة تغطّي نصفها، والرسم يحتاج المساحة
    // كاملةً على كل المقاسات لا على الجوّال وحده — والشكل المرسوم تحتها يضيع.
    measureMode: 'none',
    setMeasureMode: (mode) => {
        if (mode !== 'none') closeOverlayQuickPanel();
        set(mode === 'none' ? { measureMode: 'none' } : {
            measureMode: mode,
            infoPanelOpen: false,
            layerControlsOpen: false,
            sidebarOpen: false,
            settingsOpen: false,
            cycloneDockOpen: false,
        });
    },
    // خطوط الضغط متاحة فوق كل الطبقات، مفتوحة افتراضياً على طبقة الضغط وحدها.
    isobarsEnabled: true,
    setIsobarsEnabled: (enabled) => set({ isobarsEnabled: enabled }),
    particlesEnabled: true,
    setParticlesEnabled: (enabled) => set({ particlesEnabled: enabled }),
    legendOpen: true,
    setLegendOpen: (open) => set({ legendOpen: open }),

    // Modal state
    modalOpen: false,
    setModalOpen: (open) => {
        if (open) closeOverlayQuickPanel();
        set(open ? getResponsivePanelPatch('modalOpen') : { modalOpen: false });
    },
    modalCityName: '',
    setModalCityName: (name) => set({ modalCityName: name }),
    modalCountryName: '',
    setModalCountryName: (name) => set({ modalCountryName: name }),
    modalWeatherData: null,
    setModalWeatherData: (data) => set({ modalWeatherData: data }),
    modalLoading: false,
    setModalLoading: (loading) => set({ modalLoading: loading }),
    modalLatitude: 0,
    setModalLatitude: (lat) => set({ modalLatitude: lat }),
    modalLongitude: 0,
    setModalLongitude: (lon) => set({ modalLongitude: normalizeLongitude(lon) }),
}), {
    name: USER_PREFS_STORAGE_KEY,
    // v3: أُعيد بناء محرّك الجسيمات (محاكاة بفضاء الشاشة بأسلوب Zoom Earth) ثم أُعيدت
    // معايرة الطول/السماكة/الكثافة. القيم المحفوظة من النسخ الأقدم صار معناها مختلفاً
    // (خيوط أقصر وأرفع من المقصود)، فنُسقِطها مرّة واحدة كي تسري الافتراضيات الجديدة —
    // بقية التفضيلات (الطبقات، النموذج، الوحدات…) تبقى كما هي.
    // v6: رجع مفتاح خطوط الضغط عالمياً واحداً (isobarsEnabled) بدل مفتاح لكل طبقة
    // (isobarsByLayer، v5). الحالة المحفوظة تُترجم: أي طبقة كانت مفعّلة ⇒ مفعّل عالمياً.
    // v7: أُزيل مفتاحا «تحذيرات الأرصاد» و«خلفية الصور الجوّية» من لوحة التراكبات
    // وصارا يعملان دائماً. القيم المحفوظة تُسقَط مرّة واحدة: من أطفأهما سابقاً كان
    // سيبقى مطفأً إلى الأبد بلا مفتاح يعيدهما.
    version: 7,
    migrate: (persisted, fromVersion) => {
        const p = (persisted ?? {}) as Partial<WeatherState>
            & { isobarsEnabled?: boolean; isobarsByLayer?: Record<string, boolean> };
        if (fromVersion < 4) delete p.layerAnimationSettings;
        if (fromVersion < 5) delete p.isobarsEnabled;
        if (fromVersion < 6) {
            if (p.isobarsByLayer) p.isobarsEnabled = Object.values(p.isobarsByLayer).some(Boolean);
            delete p.isobarsByLayer;
        }
        if (fromVersion < 7) {
            delete p.alertsEnabled;
            delete p.aerialImageryEnabled;
        }
        return p as WeatherState;
    },
    storage: createJSONStorage(() => localStorage),
    // نحفظ تفضيلات المستخدم فقط — لا شيء من حالة الـ timeline إطلاقاً
    // (currentTimeIndex / isPlaying / playbackSpeed / frameFraction / timelineAutoSync).
    // ملاحظة: currentLocation لا يُحفظ هنا عمداً — الموقع الافتتاحي هو دائماً
    // "مدينة المستخدم" المكتشفة عبر IP والمحفوظة في rasid:home-location،
    // لا آخر إحداثيات تنقّل إليها على الخريطة.
    partialize: (state) => ({
        visibleLayers: state.visibleLayers,
        selectedModel: state.selectedModel,
        layerAnimationSettings: state.layerAnimationSettings,
        showHurricanes: state.showHurricanes,
        showWildfires: state.showWildfires,
        mapLabelsEnabled: state.mapLabelsEnabled,
        crosshairEnabled: state.crosshairEnabled,
        labelValuesEnabled: state.labelValuesEnabled,
        lightningEnabled: state.lightningEnabled,
        radarCoverageEnabled: state.radarCoverageEnabled,
        lightningSoundEnabled: state.lightningSoundEnabled,
        timestampBadgeEnabled: state.timestampBadgeEnabled,
        isobarsEnabled: state.isobarsEnabled,
        particlesEnabled: state.particlesEnabled,
        legendOpen: state.legendOpen,
        satelliteMode: state.satelliteMode,
        units: state.units,
        darkMode: state.darkMode,
    }),
    // دمج عميق: مفاتيح جديدة تُضاف للكود مستقبلاً تأخذ قيمها الافتراضية
    // بدل أن تختفي لأن النسخة المحفوظة أقدم منها.
    merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<WeatherState>;
        const merged = { ...current, ...p };
        if (p.visibleLayers) {
            merged.visibleLayers = { ...current.visibleLayers, ...p.visibleLayers };
        }
        if (p.layerAnimationSettings) {
            const layers = { ...current.layerAnimationSettings };
            (Object.keys(layers) as LayerKey[]).forEach((k) => {
                const saved = p.layerAnimationSettings?.[k];
                if (saved) layers[k] = { ...layers[k], ...saved };
            });
            merged.layerAnimationSettings = layers;
        }
        if (p.units) merged.units = { ...current.units, ...p.units };
        merged.visibleLayers = withLayerLink(merged.visibleLayers);
        return merged;
    },
}));
